import { Table } from 'rsuite';
import {
  generateAccountNodeId,
  getPreSelectedNode,
  getSelectedTrees,
  ITreeData,
  selectOU,
  selectOUStatus
} from '@monorepo/shared/store/fcOuSlice';
import { useEffect, useState } from 'react';
import CollaspedOutlineIcon from '@rsuite/icons/CollaspedOutline';
import ExpandOutlineIcon from '@rsuite/icons/ExpandOutline';
import { useGetUserOrgNodesQuery } from '@monorepo/shared/services/globalApi';
import { ExpandedRow, renderNormalRow } from './RenderRow';
import { useAppDispatch, useAppSelector } from '@monorepo/shared/store/hook';
import {
  getLocalLatestAccount,
  updateLocalLatestAccount,
  regionAssetApi,
  regionInstanceApi,
  regionUserHelperApi,
  useLazyGetInstByAccountQuery,
  useLazyGetAssetByAccountQuery,
  getLocalGroupOpenAccount,
  updateLocalGroupOpenAccount,
  useLazyGetInstanceExtraInfoQuery,
  MY_ACCOUNT_LIST,
  IsFullAccess
} from '@monorepo/shared';
import {
  selectAccounts,
  selectCurrentAccount,
  updateCurrentAccount
} from '@monorepo/shared/store/accountSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';

import { OrgPagination } from './OrgPagination';
import { HomeElms, server } from '../../config/const';
import { OrgToolbar } from './OrgToolbar';
import { selectPermission } from '@monorepo/shared/store/authSlice';

const Organization = () => {
  const rowKey = 'value';
  const accounts = useAppSelector(selectAccounts);
  const ouStatus = useAppSelector(selectOUStatus);
  const ouData = useAppSelector(selectOU);
  const preSelectedNode = useAppSelector(getPreSelectedNode);
  const currentGroup = useAppSelector(selectCurrentGroup);
  const [treeData, setTreeData] = useState<Record<string, any>[]>([]);
  const [tableData, setTableData] = useState<Record<string, any>[]>([]);
  const [sortColumn, setSortColumn] = useState<string>('account_id');
  const [sortType, setSortType] = useState<any>('asc');
  const [loading, setLoading] = useState<boolean>(false);
  const [sortData, setSortData] = useState<Record<string, any>[]>([]);
  const [groupSearchKeyValue, setGroupSearchKeyValue] = useState<
    Record<string, string>
  >({});
  const [emptyMessage, setEmptyMessage] = useState<string>(
    ' Please click search button to fetch data '
  );
  const groupOpenAccount = getLocalGroupOpenAccount();
  const dispatch = useAppDispatch();

  const userPermission = useAppSelector(selectPermission);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const ou = useGetUserOrgNodesQuery(
    { server: server, fcName: HomeElms?.fcName, refresh: true },
    {
      skip: userPermission === 'unset'
    }
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const handleExpanded = (rowData: any) => {
    let open = false;
    const nextExpandedRowKeys = [];

    expandedRowKeys.forEach((key) => {
      if (key === rowData[rowKey]) {
        open = true;
      } else {
        nextExpandedRowKeys.push(key);
      }
    });

    if (!open) {
      nextExpandedRowKeys.push(rowData[rowKey]);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
  };
  useEffect(() => {
    if (ouStatus === 'succeeded' && currentGroup.id === MY_ACCOUNT_LIST) {
      const data = Object.values(accounts)?.map(
        (obj: Record<string, any>, idx: number) => {
          const ouPath = treeFindPath(
            ouData,
            (treeNode: Record<string, any>) => {
              return (
                treeNode.nodeId &&
                treeNode.nodeId === generateAccountNodeId(obj)
              );
            },
            []
          );
          return {
            ...obj,
            index: idx + 1,
            parent_id: obj?.parent_id || -1,
            ouPath: ouPath,
            ou: obj?.account_company,
            regionName: obj?.region?.name || obj?.region?.alias,
            value: ouData?.length
              ? `${obj?.parent_id || -1}-${generateAccountNodeId(obj)}`
              : `${idx}-${obj?.account_id}`,
            children: [
              {
                ...obj,
                value: ouData?.length
                  ? `${obj?.parent_id}-${generateAccountNodeId(obj)}-children`
                  : `${idx}-${obj?.account_id}-children`,
                isAccount: true
              }
            ]
          };
        }
      );
      setTableData(data);
    }
  }, [accounts, ouStatus, currentGroup.id]);

  useEffect(() => {
    if (currentGroup.id !== MY_ACCOUNT_LIST) {
      setTableData([]);
      setEmptyMessage('Please click search button to fetch data');
    } else {
      setEmptyMessage('No data found');
    }
  }, [currentGroup.id]);

  useEffect(() => {
    if (sortData) {
      let newTreeData = sortData;
      if (currentGroup.id === MY_ACCOUNT_LIST && ouData) {
        const trees = ouData?.length
          ? getSelectedTrees(preSelectedNode, ouData)
          : ouData;
        const treeTable = trees.map((obj: any) =>
          generateNodeData(sortData, obj, 0, '1')
        );
        newTreeData = trees[0]?.value ? treeTable : sortData;
      }
      setTreeData(
        newTreeData.map((obj: Record<string, any>) => ({
          ...obj,
          isFullAccess: IsFullAccess(obj, HomeElms?.fcName)
        }))
      );
      const openAccount =
        currentGroup.id === MY_ACCOUNT_LIST ? currentAccount : groupOpenAccount;
      const expandedKeys = getAllExpandedKey(newTreeData, openAccount);
      setExpandedRowKeys(expandedKeys);
    }
  }, [sortData, preSelectedNode, currentGroup.id, ouData]);

  const [getInstance] = useLazyGetInstByAccountQuery();
  const [getAsset] = useLazyGetAssetByAccountQuery();
  const [getInstanceExtra] = useLazyGetInstanceExtraInfoQuery();
  useEffect(() => {
    Object.values(accounts).forEach((account) => {
      if (account.region?.token) {
        getInstance({
          account: account
        });
        getAsset({
          account: account
        });
        getInstanceExtra({ account: account });
      }
    });
  }, [accounts]);

  const getRowHeight = (rowData: any) => {
    return rowData?.isAccount ? 360 : 40;
  };
  const handleSortColumn = (sortColumn: any, sortType: any) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };
  const reloadData = async () => {
    await ou.refetch();
    dispatch(
      regionAssetApi.util.invalidateTags([
        { type: 'Asset', id: MY_ACCOUNT_LIST }
      ])
    );
    dispatch(
      regionInstanceApi.util.invalidateTags([
        { type: 'Instance', id: MY_ACCOUNT_LIST }
      ])
    );
    dispatch(
      regionUserHelperApi.util.invalidateTags([
        { type: 'Helper', id: MY_ACCOUNT_LIST }
      ])
    );
  };
  const [refreshHandler, setRefreshHandler] = useState<
    React.MouseEventHandler<HTMLElement> | undefined
  >(undefined);
  const renderEmpty = (info: any) => (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center'
      }}
    >
      {emptyMessage}
    </div>
  );

  useEffect(() => {
    if (currentGroup.id === MY_ACCOUNT_LIST) {
      setRefreshHandler(() => reloadData);
    }
  }, [currentGroup.id]);

  useEffect(() => {
    if (ou.isLoading || ou.isFetching) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [ou.isLoading, ou.isFetching]);

  return (
    <div className="organization-container">
      <div className="organization-content">
        <div className="table-container">
          <OrgToolbar
            sortColumn={sortColumn}
            sortType={sortType}
            reloadData={refreshHandler}
            tableData={tableData}
            setSortData={setSortData}
            currentGroup={currentGroup}
            setGroupSearchKeyValue={setGroupSearchKeyValue}
          />
          <Table
            loading={loading}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            isTree={true}
            cellBordered={false}
            rowKey="value"
            fillHeight
            headerHeight={40}
            rowHeight={getRowHeight}
            data={treeData?.length ? treeData : []}
            renderEmpty={renderEmpty}
            shouldUpdateScroll={false}
            onExpandChange={(isOpen, rowData) => {
              handleExpanded(rowData);
              if (isOpen) {
                if (currentGroup.id === MY_ACCOUNT_LIST) {
                  updateLocalLatestAccount(rowData);
                  dispatch(updateCurrentAccount(rowData));
                } else {
                  updateLocalGroupOpenAccount(rowData);
                }
                console.info(isOpen, '----------- rowData', rowData);
              }
            }}
            renderTreeToggle={(icon, rowData) => {
              if (rowData && rowData[rowKey] && rowData?.account_id) {
                const myIcon = expandedRowKeys.includes(rowData?.[rowKey]) ? (
                  <CollaspedOutlineIcon />
                ) : (
                  <ExpandOutlineIcon />
                );
                return myIcon;
              }
              return icon;
            }}
            renderRow={(children: any, rowData: any) => {
              if (rowData?.isAccount) {
                return <ExpandedRow rowData={rowData} />;
              }
              return children;
            }}
            expandedRowKeys={expandedRowKeys}
          >
            {renderNormalRow(
              Math.max(
                ouData?.map((o: Record<string, any>) => o?.maxLevel || 0)
              ) || 0,
              currentGroup.id !== MY_ACCOUNT_LIST
            )}
          </Table>
          {currentGroup.id !== MY_ACCOUNT_LIST && (
            <OrgPagination
              currentGroup={currentGroup}
              setRefreshHandler={setRefreshHandler}
              setTableData={setTableData}
              setLoading={setLoading}
              setEmptyMessage={setEmptyMessage}
              sortColumn={sortColumn}
              setSortColumn={setSortColumn}
              sortType={sortType}
              groupSearchKeyValue={groupSearchKeyValue}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default Organization;
const getAllExpandedKey = (
  treeData: Record<string, any>[],
  latestAccount?: Record<string, any>
): string[] => {
  const arr = treeData?.map((obj: Record<string, any>) => {
    return (obj?.hasAccount && !obj?.account_id) ||
      (obj?.account_id &&
        (latestAccount?.account_id
          ? latestAccount.account_id === obj.account_id
          : obj.user_type === 'Master' || obj.user_type === 'IAM'))
      ? [obj?.value, getAllExpandedKey(obj?.children, latestAccount)]
      : '';
  });
  const res = arr?.flat(4).filter((val) => val);
  return res;
};

const treeFindPath = (
  tree: Record<string, any>[],
  func: any,
  path: string[]
): string[] => {
  if (!tree) return [];
  for (const data of tree) {
    path.push(data?.label);
    if (func(data)) {
      return path;
    }
    if (data?.children) {
      const children = treeFindPath(data?.children, func, path);
      if (children.length) {
        return children;
      }
    }
    path.pop();
  }
  return [];
};
const generateNodeData = (
  accounts: Record<string, any>[],
  node: ITreeData,
  level: number,
  value: string
) => {
  const getAccountsNumber = (
    accounts: Record<string, any>,
    node: ITreeData
  ) => {
    return accounts.filter((obj: Record<string, any>) =>
      obj?.ouPath?.includes(node?.label)
    ).length;
  };
  const getOuName = (num: string, node: ITreeData) => {
    return num && node?.data?.node_type === 'org'
      ? `${node?.label} (${num})`
      : `${node?.label}`;
  };
  const num = getAccountsNumber(accounts, node);
  const name = getOuName(num, node);
  const subAccounts: Record<string, any>[] = node?.children
    ? node?.children.map((child, idx: number) =>
        generateNodeData(accounts, child, level + 1, `${value}-${idx + 1}`)
      )
    : [];
  const children = [...subAccounts];
  const nodeAccount =
    node?.data?.node_type === 'account'
      ? accounts.find(
          (obj: Record<string, any>) =>
            generateAccountNodeId(obj) === node?.nodeId
        ) || {}
      : {};

  return {
    ...nodeAccount,
    ou: name,
    hasAccount: num > 0,
    level: level,
    rootNode: node?.rootNode,
    value: value,
    childrenNum: children.length,
    children: children.length
      ? children
      : nodeAccount?.children?.map((obj: Record<string, any>) => ({
          ...obj,
          level: level + 1
        }))
  };
};
